import type { Task } from 'fhir/r4';
import moment from 'moment';
import React from 'react';
import { Loader, Dimmer, List, Icon, Label } from 'semantic-ui-react';

import { getCreateOrUpdatedInfo, parseTaskDueDate } from '../../newTaskList/subList/helpers';
import { STATUS_COLORS } from '../fixtures';

import {
  ActivityContainer,
  ActivityItem,
  ActivityHeader,
  ActivityHeaderLeft,
  ActivityHeaderRight,
  ActivityContent,
  ActivitySectionTitle,
  ActivityField,
  ActivityMeta,
  EmptyStateContainer,
  EmptyStateIcon,
  LoadingContainer,
} from './activityTab.styles';
import { useActivityTab } from './hooks';

type ActivityTabProps = {
  taskId?: string | undefined;
};

const ActivityTab: React.FC<ActivityTabProps> = ({ taskId }) => {
  const { taskActivities, isLoading } = useActivityTab({ taskId });

  if (isLoading) {
    return (
      <LoadingContainer>
        <Dimmer inverted active>
          <Loader>Loading activities...</Loader>
        </Dimmer>
      </LoadingContainer>
    );
  }

  if (!taskActivities?.entry || taskActivities.entry.length === 0) {
    return (
      <ActivityContainer>
        <EmptyStateContainer>
          <EmptyStateIcon>
            <Icon name="history" size="large" />
          </EmptyStateIcon>
          <p>No activity history available</p>
        </EmptyStateContainer>
      </ActivityContainer>
    );
  }

  // Sort activities by lastUpdated date (most recent first)
  const sortedActivities = taskActivities.entry
    .map((entry) => ({
      resource: entry.resource as Task,
      fullUrl: entry.fullUrl,
      search: entry.search,
    }))
    .sort((a, b) => {
      const dateA = new Date(a.resource.meta?.lastUpdated || 0);
      const dateB = new Date(b.resource.meta?.lastUpdated || 0);
      return dateB.getTime() - dateA.getTime();
    });

  // Helper function to check if notes have changed between versions
  const getNotesChanged = (currentTask: Task, previousTask?: Task) => {
    const currentNotes = currentTask.note?.filter((note) => note.authorString && note.text) || [];
    const previousNotes = previousTask?.note?.filter((note) => note.authorString && note.text) || [];

    // If no previous task, show notes if current has any
    if (!previousTask) {
      return currentNotes.length > 0;
    }

    // Compare note counts
    if (currentNotes.length !== previousNotes.length) {
      return true;
    }

    // Compare note content (text and author)
    for (let i = 0; i < currentNotes.length; i++) {
      const currentNote = currentNotes[i];
      const previousNote = previousNotes[i];

      if (
        !previousNote ||
        currentNote!.text !== previousNote.text ||
        currentNote!.authorString !== previousNote.authorString
      ) {
        return true;
      }
    }

    return false;
  };

  return (
    <ActivityContainer>
      <List divided relaxed>
        {sortedActivities.map((entry, index) => {
          const task = entry.resource;
          const isLatest = index === 0;
          const previousTask = index < sortedActivities.length - 1 ? sortedActivities[index + 1]!.resource : undefined;
          const createOrUpdatedInfo = getCreateOrUpdatedInfo(task);
          const lastUpdated = moment(task.meta?.lastUpdated).local(true).format('MM-DD-YYYY HH:mm');
          const status = task.status?.toUpperCase() || 'UNKNOWN';
          const statusColor = STATUS_COLORS[task.status] || 'grey';
          const notesChanged = getNotesChanged(task, previousTask);

          return (
            <ActivityItem key={task.id} isLatest={isLatest}>
              <List.Item>
                <List.Content>
                  <ActivityHeader>
                    <ActivityHeaderLeft>
                      <Icon name={isLatest ? 'star' : 'circle'} color={isLatest ? 'yellow' : 'grey'} size="small" />
                      <strong>{isLatest ? 'Current Version' : `Version ${sortedActivities.length - index}`}</strong>
                      <Label color={statusColor} size="mini">
                        {status}
                      </Label>
                    </ActivityHeaderLeft>
                    <ActivityHeaderRight>{lastUpdated}</ActivityHeaderRight>
                  </ActivityHeader>

                  <ActivityContent>
                    <ActivitySectionTitle>
                      <strong>Task Details:</strong>
                    </ActivitySectionTitle>

                    {task.statusReason?.text && (
                      <ActivityField>
                        <strong>Status Reason:</strong> {task.statusReason.text}
                      </ActivityField>
                    )}

                    {task.businessStatus?.text && (
                      <ActivityField>
                        <strong>Business Status:</strong> {task.businessStatus.text}
                      </ActivityField>
                    )}

                    {task.priority && (
                      <ActivityField>
                        <strong>Priority:</strong> {task.priority.toUpperCase()}
                      </ActivityField>
                    )}

                    {task.owner?.display && (
                      <ActivityField>
                        <strong>Owner:</strong> {task.owner.display}
                      </ActivityField>
                    )}

                    {task.requester?.display && (
                      <ActivityField>
                        <strong>Requester:</strong> {task.requester.display}
                      </ActivityField>
                    )}

                    {task.restriction?.recipient?.[0]?.display && (
                      <ActivityField>
                        <strong>Vendor:</strong> {task.restriction.recipient[0].display}
                      </ActivityField>
                    )}

                    {task.groupIdentifier?.value && (
                      <ActivityField>
                        <strong>Referral ID:</strong> {task.groupIdentifier.value}
                      </ActivityField>
                    )}

                    {task.identifier?.find((id) => id.system === 'https://projectwell.io/fhir/identifiers/order-id')
                      ?.value && (
                      <ActivityField>
                        <strong>Order ID:</strong>{' '}
                        {
                          task.identifier.find((id) => id.system === 'https://projectwell.io/fhir/identifiers/order-id')
                            ?.value
                        }
                      </ActivityField>
                    )}

                    {notesChanged && task.note && task.note.length > 0 && (
                      <ActivityField>
                        <strong>Notes:</strong>
                        <div style={{ marginTop: '4px' }}>
                          {(() => {
                            const firstNote = task.note.find((note) => note.authorString && note.text);
                            return firstNote ? (
                              <div
                                style={{
                                  marginBottom: '8px',
                                  padding: '8px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '4px',
                                }}
                              >
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                  <strong>{firstNote.authorString}</strong> -{' '}
                                  {moment(firstNote.time).format('MM-DD-YYYY HH:mm')}
                                </div>
                                <div style={{ fontSize: '14px' }}>{firstNote.text}</div>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </ActivityField>
                    )}

                    {(task.executionPeriod?.end || task.executionPeriod?.start) && (
                      <ActivityField>
                        <strong>Due Date:</strong>{' '}
                        {parseTaskDueDate(task.executionPeriod.end || task.executionPeriod.start!)
                          .local()
                          .format('MM-DD-YYYY HH:mm')}
                      </ActivityField>
                    )}

                    {createOrUpdatedInfo && <ActivityMeta>{createOrUpdatedInfo}</ActivityMeta>}
                  </ActivityContent>
                </List.Content>
              </List.Item>
            </ActivityItem>
          );
        })}
      </List>
    </ActivityContainer>
  );
};

export default ActivityTab;
